import React, { useState, useRef, RefObject, useEffect, useMemo } from 'react';

import { useDebounce } from '@umu/umu-react-hooks';
import { KEY_CODE } from '@umu/umu-util';
import { $document, $window } from '@umu/umu-dom';
import { If } from 'common/src/components';

import './index.scss';

import { ISearchProps, IListItem } from './interface';
import { createRoot } from 'react-dom/client';
import ThemeConfigProvider from 'common/src/config/theme/themeConfig';
const DELAY_TIME = 500;

const getBodyPaddingTop = () => {
    const bodyStyleObject = $window.getComputedStyle($document.body);
    // 获取body的paddingTop, 用于计算top值 (content-box模式下) 有可能是百分比，这里不考虑。因为 body 的 padding-top 不会设置百分比
    return bodyStyleObject.boxSizing === 'content-box' && bodyStyleObject.paddingTop.includes('px')
        ? parseFloat(bodyStyleObject.paddingTop)
        : 0;
};

export const InputSearch = (props: ISearchProps) => {
    const { style = {}, placeholder, maxLength, type = 'text', className = '', selectList, disabled = false, offset = [0, 0] } = props;
    const inputRef: RefObject<HTMLInputElement> = useRef<HTMLInputElement>(null);
    const listRef: RefObject<HTMLDivElement> = useRef(null);

    const [value, setValue] = useState('');
    const [isUlShow, setIsUlShow] = useState(false);
    const [isInputShow, setIsInputShow] = useState(!(disabled && selectList.length > 0));
    const bodyPaddingTop = useMemo(getBodyPaddingTop, []);

    useEffect(() => {
        const isHasSelectList = props.selectList && props.selectList.length === 0;
        if (isHasSelectList || !props.selectList) {
            setValue('');
        }
        setIsInputShow(isHasSelectList);
    }, [props.selectList]);

    useEffect(() => {
        const updatePopupPosition = () => {
            if (isUlShow) {
                const element = $document.getElementById('searchUlDiv');
                if (element) {
                    element.style.cssText = getUlStyle();
                }
            }
        };

        if (isUlShow) {
            $window.addEventListener('scroll', updatePopupPosition, true);
            $window.addEventListener('resize', updatePopupPosition);
        }

        return () => {
            $window.removeEventListener('scroll', updatePopupPosition, true);
            $window.removeEventListener('resize', updatePopupPosition);
        };
    }, [isUlShow]);

    const getUlList = (data: IListItem[]) => {
        return data.length > 0 ? (
            <ul className="list-table">
                {data.map((item: IListItem) => {
                    return (
                        <li
                            key={`${item.id}-ul-li`}
                            onClick={() => {
                                liOnClick(item);
                            }}
                            className="children-item"
                        >
                            {props.renderChildren(item)}
                        </li>
                    );
                })}
            </ul>
        ) : (
            <ul />
        );
    };

    const getUlStyle = () => {
        // 获取面板显示位置及样式
        let result = '';
        if (listRef.current && inputRef.current) {
            const current = listRef.current.getBoundingClientRect();
            const inputCurrent = inputRef.current.getBoundingClientRect();
            const scrollTop = $document.documentElement.scrollTop;

            const x = inputCurrent.top + inputCurrent.height + scrollTop + 1 - Number(bodyPaddingTop) + offset[1]; // 加上边框宽度1
            const y = current.left + offset[0];
            const width = current.width;
            result = `top: ${x}px; left: ${y}px; width: ${width}px`;
        }
        return result;
    };

    const createUlByList = (data: IListItem[]) => {
        if (listRef.current && inputRef.current) {
            const elementOld = $document.getElementById('searchUlDiv');
            if (elementOld) {
                $document.body.removeChild(elementOld);
            }

            const element = $document.createElement('div');
            element.id = 'searchUlDiv';
            element.className = `search-ul-list-input ${props.overlayClassName ?? ''}`;
            element.style.cssText = getUlStyle();
            const root = createRoot(element);
            root.render(<ThemeConfigProvider>{getUlList(data)}</ThemeConfigProvider>);
            $document.body.appendChild(element);
        }
    };

    const setUlShow = (isShow: boolean, data?: IListItem[]) => {
        if (isShow) {
            createUlByList(data || []);
        } else {
            const element = $document.getElementById('searchUlDiv');
            if (element) {
                $document.body.removeChild(element);
            }
        }
        setIsUlShow(isShow);
    };

    const onChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const el = e.currentTarget;
        if (!el) {
            return;
        }
        const values: string = el.value;
        setValue(values);
        getList();
    };

    const getList = useDebounce(() => {
        // 防止接口请求过快，加入截流函数
        if (props.getList) {
            props.getList(value).then((res) => {
                setUlShow(true, res);
            });
        }
    }, DELAY_TIME);

    const liOnClick = (item: IListItem) => {
        props.onSelect(item);
        setIsInputShow(false);
        onMouseLeave();
    };

    const onMouseLeave = () => {
        // 离开函数清空输入框的值同时隐藏面板
        setValue('');
        setUlShow(false);
    };

    const onBlur = () => {
        // 离开输入框的时候隐藏下拉面板
        setTimeout(() => {
            onMouseLeave();
        }, DELAY_TIME);
    };

    const onZoneClick = () => {
        if (!disabled) {
            // 区域点击事件的时候显示输入框同时滚动条滚动到最底端。
            setIsInputShow(true);
            setTimeout(() => {
                if (listRef.current) {
                    listRef.current.scrollTop = listRef.current.scrollHeight;
                }
                if (inputRef.current) {
                    inputRef.current.focus();
                }
            }, DELAY_TIME);
        }
    };

    const onFocus = () => {
        if (listRef.current) {
            // 光标聚焦的时候，滚动条位于最左侧
            listRef.current.scrollLeft = 0;
        }
    };

    const onScrollCapture = useDebounce(() => {
        if (listRef.current) {
            // 滚动条滚动的时候，跳出输入框
            if (listRef.current.scrollLeft) {
                if (inputRef.current && !inputRef.current.scrollLeft && value === '') {
                    inputRef.current.blur();
                }
            }
        }
    }, DELAY_TIME);

    const onKeyDown = (e: React.KeyboardEvent) => {
        if (disabled) {
            return;
        }
        if (!inputRef.current) {
            return;
        }
        const keyCode = e.keyCode;
        if (keyCode === KEY_CODE.KEY_ENTER) {
            if (props.getList) {
                props.getList(value).then((res) => {
                    setUlShow(true, res);
                    if (res.length > 0) {
                        liOnClick(res[0]);
                    } else {
                        onMouseLeave();
                    }
                });
            }
        }
    };

    return (
        <div style={style} className={`${className} umu-assessment-search-input`}>
            <div
                className={`select-list${isUlShow ? ' active-input' : ''}`}
                ref={listRef}
                onClick={onZoneClick}
                onScrollCapture={onScrollCapture}
            >
                {selectList.map((data: IListItem, index: number) => props.renderData(data, index))}
                <If condition={!props.maxCount || props.maxCount > selectList.length}>
                    <span>
                        <If condition={isInputShow}>
                            <input
                                ref={inputRef}
                                type={type}
                                disabled={disabled}
                                className="input"
                                placeholder={selectList.length === 0 ? placeholder : ''}
                                maxLength={maxLength}
                                onBlur={onBlur}
                                onFocus={onFocus}
                                onChange={onChange}
                                onKeyDown={onKeyDown}
                                value={value}
                            />
                        </If>
                    </span>
                </If>
            </div>
        </div>
    );
};
